import CampaignIcon from '@mui/icons-material/Campaign';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Container, Stack, ToggleButtonGroup, Typography } from '@mui/material';
import { Button, Link, ToggleButton } from '@ghs/components';
import { useUnit } from 'effector-react';
import useMountedEvent from 'hooks/useMountedEvent';
import { $$theme } from 'services/ThemeService/model';
import FeedbackPanel from 'components/FeedbackPanel/FeedbackPanel';
import CampaignDetailsSection from './CampaignDetailsSection';
import ContactDetailsSection from './ContactDetailsSection';
import { $$campaignSetup } from './model';
import SectionBox from './SectionBox';
import SelectCampaignSection from './SelectCampaignSection';
import ServicesAndOptionsSection from './ServicesAndOptionsSection';
import type { CampaignMode } from './types';

export default function CampaignSetupPage() {
  useMountedEvent($$campaignSetup.pageMounted);

  const [mode, createForm, editForm, clientOptions, cloneCampaignOptions, editCampaignOptions, contactOptions, lobOptions, stidSetup, isSubmitPending] = useUnit([
    $$campaignSetup.$mode,
    $$campaignSetup.$createForm,
    $$campaignSetup.$editForm,
    $$campaignSetup.$clientOptions,
    $$campaignSetup.$cloneCampaignOptions,
    $$campaignSetup.$editCampaignOptions,
    $$campaignSetup.$contactOptions,
    $$campaignSetup.$lobOptions,
    $$campaignSetup.$stidSetup,
    $$campaignSetup.$isSubmitPending
  ]);
  const { palette } = useUnit($$theme.$palette);

  const isCreate = mode === 'create';

  return (
    <Container id="campaign-setup-container" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 0, mb: 1 }}>
        <Box>
          <Typography variant="h4" color="text.primary" sx={{ mt: 0.5 }}>
            {isCreate ? 'Campaign Setup' : 'Edit Campaign'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isCreate ? (
              <>
                Set up a new campaign. Fields marked <span style={{ color: palette.error.main }}>*</span> are required.
              </>
            ) : (
              <>
                Update an existing campaign. Fields marked <span style={{ color: palette.error.main }}>*</span> are required.
              </>
            )}
          </Typography>
        </Box>

        <Stack alignItems="flex-end" spacing={0.5}>
          <Link href="#" variant="caption" underline="hover">
            Link to MTSelect
          </Link>
          <Link href="#" variant="caption" underline="hover">
            Link to Edit Campaigns/Versions
          </Link>
        </Stack>
      </Stack>

      {/* Mode toggle */}
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={mode}
        onChange={(_, val: CampaignMode) => {
          if (val) $$campaignSetup.modeChanged(val);
        }}
        sx={{ mb: 0 }}
      >
        <ToggleButton value="create" sx={{ gap: 1 }}>
          <CampaignIcon fontSize="small" />
          Create New Campaign
        </ToggleButton>
        <ToggleButton value="edit" sx={{ gap: 1 }}>
          <TuneIcon fontSize="small" />
          Edit Existing Campaign
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Feedback panel */}
      <Box sx={{ mb: 0 }}>
        <FeedbackPanel model={$$campaignSetup.feedbackPanel} />
      </Box>

      {/* Form */}
      {isCreate ? (
        <>
          <SectionBox title="Campaign Details">
            <CampaignDetailsSection
              formData={createForm}
              clientOptions={clientOptions}
              campaignOptions={cloneCampaignOptions}
              lineOfBusinessOptions={lobOptions}
              onChange={patch => $$campaignSetup.createFormChanged(patch)}
            />
          </SectionBox>

          <SectionBox title="Contact Details">
            <ContactDetailsSection formData={createForm} contactOptions={contactOptions} onChange={patch => $$campaignSetup.createFormChanged(patch)} />
          </SectionBox>

          <SectionBox title="Services & Options">
            <ServicesAndOptionsSection formData={createForm} onChange={patch => $$campaignSetup.createFormChanged(patch)} stidSetup={stidSetup} />
          </SectionBox>
        </>
      ) : (
        <SectionBox title="Select Campaign">
          <SelectCampaignSection formData={editForm} clientOptions={clientOptions} campaignOptions={editCampaignOptions} onChange={patch => $$campaignSetup.editFormChanged(patch)} />
        </SectionBox>
      )}

      {/* Action bar */}
      <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3, pb: 2 }}>
        <Button variant="outlined" onClick={() => $$campaignSetup.cancelButtonClicked()}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => $$campaignSetup.submitButtonClicked()} disabled={isSubmitPending}>
          Submit
        </Button>
      </Stack>
    </Container>
  );
}
